import {Injectable} from '@angular/core';
import {Cart, OrderEntry} from '@spartacus/cart/base/root';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {DcpActiveCartService} from '../../services/dcp-active-cart.service';
import {DcpOrderEntryService} from '../../services/dcp-order-entry.service';

@Injectable({
  providedIn: 'root',
})
export class CheckoutEntryValidationService {
  constructor(
    protected activeCartService: DcpActiveCartService,
    protected dcpOrderEntryService: DcpOrderEntryService
  ) {}

  /**
   * Checks if any of orderEntries is marked as invalid.
   *
   * @param entries  list of entries to check
   */
  public containsInvalidEntries(entries: OrderEntry[]): boolean {
    return entries.some((e) => this.isEntryInvalid(e));
  }

  public isCartInvalid(cart: Cart): boolean {
    return !!this.getCartEntriesWithErrors(cart)?.length;
  }

  public isActiveCartInvalid(): Observable<boolean> {
    return this.activeCartService.getActiveCart().pipe(map((cart) => this.isCartInvalid(cart)));
  }

  /**
   * Checks if an orderEntry is marked as invalid.
   * If invalid is not set false will be returned.
   *
   * @param entry to check
   */
  public isEntryInvalid(entry: OrderEntry): boolean {
    return !!entry?.invalid || this.isEntryMissingPriceInformation(entry);
  }

  private isEntryMissingPriceInformation(entry: OrderEntry) {
    const hasZeroPriceAllowedCta: boolean = entry?.product?.transactionTypes.some((tt) => tt.isZeroPriceAllowed);

    const missingPriceInformation =
      !this.dcpOrderEntryService.getSubtotal(entry) ||
      !this.dcpOrderEntryService.getPriceDetails(entry) ||
      (hasZeroPriceAllowedCta ? false : !this.dcpOrderEntryService.getPriceComponents(entry));

    // configurable products do not require price information
    return entry?.product?.configurable ? false : missingPriceInformation;
  }

  /**
   * Checks if the cart is error prone.
   *
   * @param cart to check
   */
  public getCartEntriesWithErrors(cart: Cart): OrderEntry[] {
    return this.filterEntriesWithErrors(cart?.entries);
  }

  public filterEntriesWithErrors(entries: OrderEntry[]): OrderEntry[] {
    return entries?.filter((e) => this.isEntryInvalid(e));
  }
}
